import { ConfigurationProps } from '../Configuration';
import { Scroll } from '../General';
import { KeyboardTrigger } from '../Layout';
import {
	from_unit,
	render_length_unit,
	render_length,
	UNITS,
	getChangeWrapperNumber,
	getChangeWrapperString,
	UseStateObject,
	ConfigSetter,
	renderInputLength,
	useContainerSize,
} from '../shared/shared';
import { v4 as uuidv4 } from 'uuid';

import ContentItem, {
	ButtonProps,
	ContentFooter,
	ContentItemProps,
	useConfirm,
} from './ContentItem';
import { SummaryItemCell, SummaryScroll, ItemIterator, duplicateConfiguration } from './Shared';
import { PALLETCORNERS } from './layout_utils';
import {
	getPalletDimensions,
	Pallet_t,
	PalletDimensions,
	Box_t,
} from 'blackbox';
import { UnitsSystem } from 'libapi';
import React, { useState, useMemo } from 'react';
import { LayoutModelProps, LayoutModel } from './Layouts/canvas/LayoutModel';

const defaultPallet = (index: number): Pallet_t => {
	let cp = {
		name: 'Pallet ' + String(index),
		id: String(uuidv4()),
		point: { x: 0, y: 0, z: 0, θ: 0 },
		Layouts: [],
		Stack: [],
		Detection: {
			Top: 0,
			TopEnabled: false,
			Bottom: 0,
			BottomEnabled: false,
		},
		Dimensions: { width: 1016, length: 1219, height: 45 },
	} as Pallet_t;
	return cp;
};

interface PalletModelProps {
	pallet: Pallet_t;
	corner?: PALLETCORNERS;
	allBoxes: Box_t[];
	unitsSystem: UnitsSystem;
}

export function PalletModel({
	pallet,
	corner,
	allBoxes,
	unitsSystem,
}: PalletModelProps) {
	const { ref: lContainerRef, rect: lContainerRect } =
			useContainerSize<HTMLDivElement>(),
		lSize = useMemo(
			function () {
				if (!lContainerRect) return 0;
				return Math.min(lContainerRect.width, lContainerRect.height);
			},
			[lContainerRect]
		),
		layoutProps: LayoutModelProps = {
			size: lSize,
			pallet: pallet,
			allBoxes,
			unitsSystem,
			snapStrength: 0,
      overhang: 0
		};
	return (
		<div className="full-size flex-center">
			<figure className="full-width height-80 flex-center " ref={lContainerRef}>
				{lContainerRect && <LayoutModel {...layoutProps} />}
			</figure>
		</div>
	);
}

interface PalletCellProps {
	pallet: Pallet_t;
	startEdit: () => void;
	deletePallet: () => void;
  duplicatePallet: () => void;
	allBoxes: Box_t[];
	unitsSystem: UnitsSystem;
}

function PalletCell({
	pallet,
	startEdit,
	deletePallet,
  duplicatePallet,
	allBoxes,
	unitsSystem,
}: PalletCellProps) {
	const { ref: lContainerRef, rect: lContainerRect } =
			useContainerSize<HTMLDivElement>(),
		lSize = useMemo(
			function () {
				if (!lContainerRect) return 0;
				return Math.min(lContainerRect.width, lContainerRect.height);
			},
			[lContainerRect]
		),
		{ width, length, height } = getPalletDimensions(pallet),
		model_props = {
			pallet,
			size: lSize,
			allBoxes,
			unitsSystem,
			snapStrength: 0,
      overhang: 0
		},
		properties = `${render_length(width, unitsSystem)} x ${render_length(
			length,
			unitsSystem
		)} ${render_length_unit(unitsSystem)} x ${render_length(
			height,
			unitsSystem
		)} ${render_length_unit(unitsSystem)}`;
	return (
		<SummaryItemCell
			name={pallet.name}
			properties={properties}
			trashAction={deletePallet}
			editAction={startEdit}
      duplicateAction={duplicatePallet}
		>
			<div className="full-size flex-center" ref={lContainerRef}>
				{lContainerRect && <LayoutModel {...model_props} />}
			</div>
		</SummaryItemCell>
	);
}

interface DimensionInputProps {
	Dimensions: PalletDimensions;
	updateDimensions: (d: PalletDimensions) => void;
	Title: string;
	units_system: UnitsSystem;
	triggerKeyboard: KeyboardTrigger;
}

function capitalizeFirstLetter(pString: string) {
	return pString.charAt(0).toUpperCase() + pString.slice(1);
}

function DimensionInput({
	Dimensions,
	updateDimensions,
	Title,
	units_system,
	triggerKeyboard,
}: DimensionInputProps) {
	if (!Dimensions) Dimensions = { width: 0, length: 0, height: 0 };
	const lUpdatePalletDimension =
			(pDimension: keyof PalletDimensions) => (pNewValue: number) => {
				if(pNewValue <= 0) return;
				const lCp = { ...Dimensions };
				if (UnitsSystem.IMPERIAL === units_system)
					pNewValue = from_unit(pNewValue, UNITS.inches);
				lCp[pDimension] = pNewValue;
				updateDimensions(lCp);
			},
		lGetInputHandler = function (pDimension: keyof PalletDimensions) {
			return getChangeWrapperNumber(lUpdatePalletDimension(pDimension));
		},
		lGetTrigger = function (
			pDimension: keyof PalletDimensions,
			pHandler: (pString: string) => void
		) {
			return function () {
				if (!triggerKeyboard) return;
				triggerKeyboard(
					`Enter pallet ${pDimension} dimension (${render_length_unit(
						units_system
					)})`,
					String(render_length(Dimensions[pDimension], units_system)),
					pHandler
				);
			};
		};
	const lDimensions: (keyof PalletDimensions)[] = ['width', 'length', 'height'];
	return (
		<div className="full-size flex-col">
			{lDimensions.map(function (pDim, pIndex) {
				const lInputHandler = lGetInputHandler(pDim);
				return (
					<div
						className=" full-width flex-col double-margin-bottom double-margin-top"
						key={pIndex}
					>
						<div className="bold-font margin-bottom">
							<span>{`${capitalizeFirstLetter(pDim)} (${render_length_unit(
								units_system
							)})`}</span>
						</div>

						<input
							className="full-width"
							type="number"
							value={renderInputLength(Dimensions[pDim], units_system)}
							onChange={lInputHandler.Event}
							onClick={lGetTrigger(pDim, lInputHandler.FromString)}
						/>
					</div>
				);
			})}
		</div>
	);
}

enum PalletSteps {
	Dimensions,
	Summary,
}

type PalletsSetter = ConfigSetter<Pallet_t[]>;

interface PalletsHook {
	Step: UseStateObject<PalletSteps>;
	EditingPallet: UseStateObject<Pallet_t>;
	EditingIndex: UseStateObject<number>;
	LeftButton: ButtonProps;
	RightButton: ButtonProps;
	StartEdit: (pIndex: number) => void;
}
const usePallets = (
	allPallets: Pallet_t[],
	setPallets: PalletsSetter,
	handleBack: () => void,
	handleNext: () => void
): PalletsHook => {
	const [step, set_step] = useState<PalletSteps>(PalletSteps.Summary);
	const [editingPallet, setEditingPallet] = useState<Pallet_t>(
		defaultPallet(allPallets.length + 1)
	);
	const [editingIndex, setEditingIndex] = useState<number>(allPallets.length);
	const lLeftButton: ButtonProps = {
		name: step === PalletSteps.Summary ? '' : 'Back',
		action: () => {
			if (PalletSteps.Summary === step) {
				handleBack();
				return;
			}
			if (allPallets.length > 0) {
				set_step(PalletSteps.Summary);
				return;
			}
			handleBack();
		},
		enabled: step !== PalletSteps.Summary,
	};
	function palletNameUnique() {
		const lAllNames = allPallets.map(p => p.name);
		for (let i = 0; i < lAllNames.length; i++) {
			if (i !== editingIndex && editingPallet.name === lAllNames[i]) {
				return false;
			}
		}
		return true;
	}
	const lRightButton: ButtonProps = {
			name: step === PalletSteps.Summary ? 'Create a Layout' : 'Save',
			enabled: step === PalletSteps.Summary ? allPallets.length > 0 : true,
			restrictionCheck: function () {
				if (!palletNameUnique()) {
					return 'Pallet name must be unique.';
				}else if(editingPallet.Dimensions.width <= 0 || editingPallet.Dimensions.length <= 0 || editingPallet.Dimensions.height <= 0) {
					return 'Pallet dimensions must be greater than 0.';
				}
				return void 0;
			},
			action: () => {
				if (PalletSteps.Summary === step) {
					handleNext();
					return;
				}
				let temp: Pallet_t = { ...editingPallet };
				temp.Layouts = [];
				temp.Stack = [];
				let newPallets: Pallet_t[] = [];
				if (editingIndex > allPallets.length || allPallets.length === 0) {
					newPallets = [...allPallets, temp];
				} else {
					allPallets.forEach((p: Pallet_t, i: number) => {
						if (i === editingIndex) {
							newPallets.push(editingPallet);
						} else {
							newPallets.push(p);
						}
					});
				}
				setPallets(newPallets);
				// short circuit to summary.
				set_step(PalletSteps.Summary);
			},
		},
		lStartEdit = function (pIndex: number) {
			if (pIndex >= 0) {
				setEditingPallet(allPallets[pIndex]);
				setEditingIndex(pIndex);
			} else {
				setEditingIndex(allPallets.length + 1);
				setEditingPallet(defaultPallet(allPallets.length + 1));
			}
			// set_step(PalletSteps.Jog);
			set_step(PalletSteps.Dimensions);
		};

	return {
		Step: { Current: step, Set: set_step },
		EditingIndex: { Current: editingIndex, Set: setEditingIndex },
		EditingPallet: { Current: editingPallet, Set: setEditingPallet },
		LeftButton: lLeftButton,
		RightButton: lRightButton,
		StartEdit: lStartEdit,
	};
};

export default function Pallets({
	instructionNumber,
	allPallets,
	handleNext,
	handleBack,
	setPallets,
	allBoxes,
	unitsSystem,
	triggerKeyboard,
}: ConfigurationProps) {
	let instruction: string;
	const lPalletsHook = usePallets(
			allPallets,
			setPallets,
			handleBack,
			handleNext
		),
		LeftButton = lPalletsHook.LeftButton,
		RightButton = lPalletsHook.RightButton,
		startEdit = (pIndex: number) => () => lPalletsHook.StartEdit(pIndex),
		step = lPalletsHook.Step.Current,
		editingPallet = lPalletsHook.EditingPallet.Current,
		setEditingPallet = lPalletsHook.EditingPallet.Set,
		[lTriggerConfirm, lConfirmProps] = useConfirm([lPalletsHook.Step.Current]),
		deletePallet = (pPalletIndex: number) => () =>
			lTriggerConfirm(function () {
				let cp = [...allPallets];
				cp.splice(pPalletIndex, 1);
				setPallets(cp);
			}, `Are you sure you want to delete ${allPallets[pPalletIndex].name}?`),
		lItemIterator = useMemo(
			function (): ItemIterator {
				return {
					pGet: function (
						pStartIndex: number,
						pEndOffset: number,
						pSearchFilter: string | undefined
					) {
						let lPallets = [...allPallets].map(function (pPallet, pIndex) {
							return [pPallet, pIndex] as [Pallet_t, number];
						});
						if (pSearchFilter) {
							const lRegExp = new RegExp(pSearchFilter, 'i');
							lPallets = lPallets.filter(([pPallet, pPalletIndex]) =>
								lRegExp.test(pPallet.name)
							);
						}
						const lItemCount = lPallets.length;
						return [
							lPallets
								.splice(pStartIndex, pEndOffset)
								.map((pPalletPair: [Pallet_t, number], pRelativeIndex: number) => {
									const lIndex = pPalletPair[1],
										palletCellProps: PalletCellProps = {
											pallet: pPalletPair[0],
											startEdit: startEdit(lIndex),
											deletePallet: deletePallet(lIndex),
                      duplicatePallet: duplicateConfiguration(lIndex, allPallets, setPallets),
											allBoxes,
											unitsSystem: unitsSystem,
										};
									return <PalletCell key={pRelativeIndex} {...palletCellProps} />;
								}),
							lItemCount,
						];
					},
					triggerKeyboard: triggerKeyboard,
				};
			},
			[allPallets]
		);

	switch (step) {
		case PalletSteps.Summary: {
			instruction = 'Create And Edit Pallets';
			const AddButton: ButtonProps = {
				name: 'Add new pallet',
				action: startEdit(-1),
			};
			const contentItemProps = {
				instruction,
				instructionNumber,
				LeftButton,
				RightButton,
				AddButton,
				sub_step: 1,
				isSummary: true,
				sub_step_count: 3,
				confirmAction: lConfirmProps,
			};
			const summaryProps = {
				...lItemIterator,
				instruction,
				AddButton,
				RightFooterButton: RightButton,
				LeftFooterButton: LeftButton,
				ConfirmFooter: lConfirmProps,
			};
			return (
				<div className="full-size flex-center align-center">
					<SummaryScroll {...summaryProps} />
				</div>
			);
		}

		case PalletSteps.Dimensions: {
			instruction = 'Enter Pallet Dimensions';
			const pallet_model: DimensionInputProps = {
				Title: 'Pallet',
				Dimensions: editingPallet.Dimensions,
				updateDimensions: (d: PalletDimensions) => {
					let cp = { ...editingPallet };
					cp.Dimensions = d;
					setEditingPallet(cp);
				},
				units_system: unitsSystem,
				triggerKeyboard: triggerKeyboard,
			};

			const pm: PalletModelProps = {
				pallet: editingPallet,
				/* corner: PALLETCORNERS.BOTTOM_LEFT, */
				allBoxes: allBoxes,
				unitsSystem: unitsSystem,
			};

			const updatePalletName = function (pNewName: string) {
				let lCp = { ...editingPallet };
				lCp.name = pNewName;
				setEditingPallet(lCp);
			};

			const editProps: EditPalletProps = {
				pPalletName: editingPallet.name,
				pUpdateName: updatePalletName,
				pDimensionInputProps: pallet_model,
				triggerKeyboard: triggerKeyboard,
				pPalletModelProps: pm,
				instructionNumber,
				LeftButton,
				RightButton,
				sub_step: 1,
				sub_step_count: 3,
				instruction,
			};
			return <EditPallet {...editProps} />;
		}
	}
}

interface EditPalletProps extends ContentItemProps {
	pPalletName: string;
	pUpdateName: (pNewName: string) => void;
	pPalletModelProps: PalletModelProps;
	pDimensionInputProps: DimensionInputProps;
	triggerKeyboard: KeyboardTrigger;
}

function EditPallet(pProps: EditPalletProps) {
	const {
		pPalletName,
		pUpdateName,
		pPalletModelProps,
		pDimensionInputProps,
		triggerKeyboard,
	} = pProps;

	const lNameHandlers = getChangeWrapperString(pUpdateName),
		lTriggerKeyboardName = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					'Enter a name for your Pallet:',
					pPalletName,
					lNameHandlers.Direct
				);
			}
		};

	return (
		<ContentItem {...pProps}>
			<div className="full-size flex-center align-start color-blue">
				<div className="grid-col-70 flex-between full-height full-width">
					<div className="BoxConfigurator">
						<PalletModel {...pPalletModelProps} />
						<ContentFooter
							RightButton={pProps.RightButton}
							LeftButton={pProps.LeftButton}
							confirmAction={pProps.confirmAction}
						/>
					</div>
					<Scroll className="full-size border-left">
						<div className="flex-col align-center full-width">
							<div className="full-width-minus-2pad flex-col align-start double-margin-top double-margin-bottom">
								<span className="bold-font margin-bottom">Pallet Name</span>
								<input
									className="full-width"
									type="text"
									value={pPalletName}
									onChange={lNameHandlers.Event}
									onClick={lTriggerKeyboardName}
								/>
							</div>
							<div className="full-width-minus-2pad flex-col align-start">
								<DimensionInput {...pDimensionInputProps} />
							</div>
						</div>
					</Scroll>
				</div>
			</div>
		</ContentItem>
	);
}
